import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Rocket, Search, Filter, ChevronDown, ChevronUp, AlertTriangle,
  Building2, Mail, Phone, Calendar as CalIcon, Euro,
} from "lucide-react";
import BackButton from "../components/BackButton";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const SOURCE_OPTS = [
  { v: "linkedin", l: "LinkedIn" }, { v: "congress", l: "Congresso" },
  { v: "referral", l: "Referral" }, { v: "cold_outreach", l: "Cold outreach" },
  { v: "inbound", l: "Inbound" }, { v: "other", l: "Altro" },
];

function fmtEur(n) {
  if (n == null) return "—";
  try {
    return "€ " + Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } catch { return `€ ${n}`; }
}

function LeadCard({ lead, onOpen, onDragStart }) {
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, lead.id)}
      onClick={() => onOpen(lead)}
      className="rounded-lg border bg-white p-3 mb-2 cursor-pointer hover:shadow-sm transition select-none"
      style={{ borderColor: "var(--pxos-border)" }}
      data-testid={`crm-card-${lead.id}`}
    >
      <div className="font-medium truncate" style={{ color: "var(--pxos-text-dark)" }}>{lead.contact_name}</div>
      {lead.business_name && (
        <div className="text-xs opacity-70 truncate flex items-center gap-1">
          <Building2 size={10} /> {lead.business_name}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-1 mt-2">
        {lead.potential_value != null && (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
            <Euro size={10} /> {fmtEur(lead.potential_value)}
          </span>
        )}
        {lead.probability_percent != null && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-sky-50 text-sky-700">
            {lead.probability_percent}%
          </span>
        )}
      </div>
      {lead.next_action && (
        <div className="mt-2 text-xs opacity-80 truncate">
          <CalIcon size={10} className="inline mr-1" />
          {lead.next_action}
          {lead.next_action_date && <span className="opacity-60"> · {new Date(lead.next_action_date).toLocaleDateString("it-IT")}</span>}
        </div>
      )}
      {lead.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {lead.tags.slice(0, 3).map((t) => (
            <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700">{t}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function NewLeadDialog({ open, onOpenChange, stages, onCreated }) {
  const [form, setForm] = useState({
    contact_name: "", business_name: "", role: "", source: "", email: "",
    phone: "", city: "", potential_value: "", probability_percent: "",
    next_action: "", next_action_date: "", notes: "",
    stage_id: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && stages.length > 0 && !form.stage_id) {
      setForm((f) => ({ ...f, stage_id: stages[0].id }));
    }
  }, [open, stages]);

  async function submit() {
    if (!form.contact_name.trim()) {
      toast.error("Nome contatto obbligatorio");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] === undefined) delete payload[k];
      });
      if (payload.potential_value != null)
        payload.potential_value = parseFloat(payload.potential_value);
      if (payload.probability_percent != null)
        payload.probability_percent = parseInt(payload.probability_percent);
      const created = await api.crmLeadCreate(payload);
      toast.success("Lead creato");
      onCreated?.(created);
      onOpenChange(false);
      setForm({ contact_name: "", business_name: "", role: "", source: "", email: "",
                phone: "", city: "", potential_value: "", probability_percent: "",
                next_action: "", next_action_date: "", notes: "", stage_id: stages[0]?.id || "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore creazione");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="crm-new-lead-dialog" className="sm:max-w-lg">
        <DialogHeader><DialogTitle>Nuovo lead</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto">
          <div>
            <Label>Nome contatto *</Label>
            <Input value={form.contact_name}
                   onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                   data-testid="crm-lead-contact-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Azienda/Studio</Label>
              <Input value={form.business_name}
                     onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                     data-testid="crm-lead-business" /></div>
            <div><Label>Ruolo</Label>
              <Input value={form.role}
                     onChange={(e) => setForm({ ...form, role: e.target.value })}
                     data-testid="crm-lead-role" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Email</Label>
              <Input type="email" value={form.email}
                     onChange={(e) => setForm({ ...form, email: e.target.value })}
                     data-testid="crm-lead-email" /></div>
            <div><Label>Telefono</Label>
              <Input value={form.phone}
                     onChange={(e) => setForm({ ...form, phone: e.target.value })}
                     data-testid="crm-lead-phone" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Città</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div><Label>Fonte</Label>
              <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                <SelectTrigger data-testid="crm-lead-source"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {SOURCE_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Valore potenziale (€)</Label>
              <Input type="number" step="0.01" value={form.potential_value}
                     onChange={(e) => setForm({ ...form, potential_value: e.target.value })} /></div>
            <div><Label>Probabilità (%)</Label>
              <Input type="number" min="0" max="100" value={form.probability_percent}
                     onChange={(e) => setForm({ ...form, probability_percent: e.target.value })} /></div>
          </div>
          <div>
            <Label>Stage iniziale</Label>
            <Select value={form.stage_id} onValueChange={(v) => setForm({ ...form, stage_id: v })}>
              <SelectTrigger data-testid="crm-lead-stage"><SelectValue /></SelectTrigger>
              <SelectContent>
                {stages.map((st) => <SelectItem key={st.id} value={st.id}>{st.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Prossima azione</Label>
              <Input value={form.next_action}
                     onChange={(e) => setForm({ ...form, next_action: e.target.value })} /></div>
            <div><Label>Data prossima azione</Label>
              <Input type="date" value={form.next_action_date}
                     onChange={(e) => setForm({ ...form, next_action_date: e.target.value })} /></div>
          </div>
          <div><Label>Note</Label>
            <Textarea rows={2} value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={submit} disabled={saving} data-testid="crm-lead-submit">
            {saving ? "Salvataggio…" : "Crea lead"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CrmKanban() {
  const nav = useNavigate();
  const [stages, setStages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [alerts, setAlerts] = useState([]);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  async function loadAll() {
    setLoading(true);
    try {
      const [stagesR, leadsR, statsR, alertsR] = await Promise.all([
        api.crmStages(), api.crmLeads({ limit: 500 }),
        api.crmStats(), api.crmAlerts().catch(() => ({ alerts: [] })),
      ]);
      setStages(stagesR || []);
      setLeads(leadsR || []);
      setAlerts(alertsR.alerts || []);
      return { stages: stagesR, stats: statsR };
    } catch (e) {
      toast.error("Errore caricamento CRM");
      return null;
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadAll(); }, []);

  const leadsByStage = useMemo(() => {
    const q = search.toLowerCase().trim();
    const map = {};
    for (const st of stages) map[st.id] = [];
    for (const l of leads) {
      if (q) {
        const hay = `${l.contact_name} ${l.business_name || ""} ${l.city || ""} ${l.email || ""}`.toLowerCase();
        if (!hay.includes(q)) continue;
      }
      (map[l.stage_id] ||= []).push(l);
    }
    return map;
  }, [leads, stages, search]);

  const totalValue = useMemo(
    () => leads.filter((l) => !stages.find((s) => s.id === l.stage_id)?.is_lost)
                .reduce((acc, l) => acc + (l.potential_value || 0), 0),
    [leads, stages],
  );

  function onDragStart(e, leadId) {
    e.dataTransfer.setData("text/plain", leadId);
  }

  async function onDrop(e, stageId) {
    e.preventDefault();
    const leadId = e.dataTransfer.getData("text/plain");
    if (!leadId) return;
    const lead = leads.find((l) => l.id === leadId);
    if (!lead || lead.stage_id === stageId) return;
    // optimistic
    setLeads((prev) => prev.map((l) => l.id === leadId ? { ...l, stage_id: stageId } : l));
    try {
      await api.crmMoveStage(leadId, { stage_id: stageId });
      toast.success("Lead spostato");
    } catch {
      toast.error("Errore spostamento — ricarico");
      loadAll();
    }
  }

  return (
    <div className="space-y-4" data-testid="crm-page">
      <BackButton to="/" testid="crm-back-btn" />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <Rocket size={22} className="opacity-70" />
            <h1 className="font-display text-3xl sm:text-4xl font-semibold">CRM Prime Vision</h1>
          </div>
          <p className="mt-1 text-sm opacity-70">
            Pipeline lead · {leads.length} totali · Valore aperto: <strong>{fmtEur(totalValue)}</strong>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 opacity-50" />
            <Input placeholder="Cerca…" value={search} onChange={(e) => setSearch(e.target.value)}
                   className="pl-8 w-52" data-testid="crm-search" />
          </div>
          <Button variant="default" onClick={() => setDialogOpen(true)} data-testid="crm-new-lead-btn">
            <Plus size={14} className="mr-1" /> Nuovo lead
          </Button>
        </div>
      </div>

      {alerts.length > 0 && (
        <div className="pxos-card p-3" data-testid="crm-alerts-card">
          <button
            type="button"
            onClick={() => setAlertsOpen((v) => !v)}
            className="flex items-center justify-between w-full text-sm font-medium"
            data-testid="crm-alerts-toggle"
          >
            <span className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-amber-600" />
              Attenzioni CRM · {alerts.length}
            </span>
            {alertsOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {alertsOpen && (
            <ul className="mt-3 space-y-1.5 text-sm" data-testid="crm-alerts-list">
              {alerts.slice(0, 20).map((a, i) => (
                <li key={`${a.kind}-${a.lead_id}-${i}`}
                    className="flex items-center justify-between border-b py-1.5"
                    style={{ borderColor: "var(--pxos-border)" }}>
                  <div>
                    <span className={a.severity === "warning" ? "text-amber-700" : ""}>{a.title}</span>
                  </div>
                  {a.lead_id && (
                    <button className="text-xs text-sky-700 hover:underline"
                            onClick={() => nav(`/prime-vision/crm/${a.lead_id}`)}>
                      Apri lead
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {loading ? (
        <div className="pxos-card p-8 text-center opacity-70">Caricamento…</div>
      ) : (
        <div className="overflow-x-auto pb-2" data-testid="crm-board">
          <div className="flex gap-3 min-w-max">
            {stages.map((st) => (
              <div
                key={st.id}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => onDrop(e, st.id)}
                className="w-72 shrink-0 rounded-xl border p-3"
                style={{ borderColor: "var(--pxos-border)", background: "rgba(0,0,0,0.02)" }}
                data-testid={`crm-column-${st.id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: st.color || "#B8B8B8" }} />
                    <span className="text-sm font-semibold" style={{ color: "var(--pxos-text-dark)" }}>{st.name}</span>
                  </div>
                  <span className="text-xs opacity-60">{leadsByStage[st.id]?.length || 0}</span>
                </div>
                <div className="min-h-[100px]">
                  {(leadsByStage[st.id] || []).length === 0 ? (
                    <div className="text-xs opacity-40 italic text-center py-4">Trascina qui</div>
                  ) : (
                    (leadsByStage[st.id] || []).map((l) => (
                      <LeadCard key={l.id} lead={l}
                                 onOpen={(x) => nav(`/prime-vision/crm/${x.id}`)}
                                 onDragStart={onDragStart} />
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <NewLeadDialog open={dialogOpen} onOpenChange={setDialogOpen}
                     stages={stages}
                     onCreated={(l) => setLeads((prev) => [l, ...prev])} />
    </div>
  );
}
